import * as React from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';
import { cn } from '@client/lib/utils';

type Tone = 'good' | 'critical' | 'info';
interface Toast { id: number; tone: Tone; title: string; body?: string; action?: { label: string; onClick: () => void } }
interface Ctx { push: (t: Omit<Toast, 'id'>) => void }

const ToastContext = React.createContext<Ctx>({ push: () => undefined });

export function useToast() {
  return React.useContext(ToastContext);
}

/** RxToast: floats above everything (z-toast), row radius, panel shadow, auto-dismiss after 5 s. */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = React.useState<Toast[]>([]);
  const push = React.useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.random();
    setItems((prev) => [...prev.slice(-3), { ...t, id }]);
    window.setTimeout(() => setItems((prev) => prev.filter((i) => i.id !== id)), t.tone === 'critical' ? 8000 : 5000);
  }, []);
  const ctx = React.useMemo(() => ({ push }), [push]);
  return (
    <ToastContext.Provider value={ctx}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[1100] flex w-[min(380px,calc(100%-32px))] flex-col gap-2" aria-live="polite">
        {items.map((t) => {
          const Icon = t.tone === 'good' ? CheckCircle2 : t.tone === 'critical' ? AlertCircle : Info;
          return (
            <div key={t.id} className={cn('pointer-events-auto flex items-start gap-3 rounded-row bg-popover p-3.5 shadow-panel animate-pop dark:border dark:border-border')} role="status">
              <Icon className={cn('mt-0.5 size-4 shrink-0', t.tone === 'good' && 'text-good', t.tone === 'critical' && 'text-critical', t.tone === 'info' && 'text-brand')} aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-[600] text-foreground">{t.title}</p>
                {t.body ? <p className="mt-0.5 break-words text-[12.5px] text-muted">{t.body}</p> : null}
                {t.action ? <button className="mt-1.5 text-[12.5px] font-[600] text-link hover:underline" onClick={() => { t.action?.onClick(); setItems((prev) => prev.filter((i) => i.id !== t.id)); }}>{t.action.label}</button> : null}
              </div>
              <button className="rounded-full p-0.5 text-muted hover:bg-row-hover hover:text-foreground" onClick={() => setItems((prev) => prev.filter((i) => i.id !== t.id))} aria-label="Dismiss">
                <X className="size-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
